// BL-241 Gate 5 — pure helpers for outbound compose-new.
//
// The UI creates or reuses a CRM contact, creates an intake session shell, then reuses the
// existing staff-send-reply path for the first message. Nothing here sends provider traffic
// or starts any automated/AI outbound flow.
import { formatPhone } from './phone';
import { RETIRED_SMS_LINE } from './smsSenderSelection';

const TERMINAL_APPROVAL_STATUSES = ['approved', 'declined', 'closed'];
const WHATSAPP_FREEFORM_WINDOW_MS = 24 * 60 * 60 * 1000;
const GSM_BASIC = new Set([
    ...'@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà',
]);
const GSM_EXTENSION = new Set(['^', '{', '}', '\\', '[', '~', ']', '|', '€']);

export const COMPOSE_MESSAGE_TYPES = ['general', 'template', 'campaign', 'service_update'];

export function isCampaignAvailable(recipientCount = 0) {
    return Number(recipientCount) >= 2;
}

export function countSmsSegments(value = '') {
    const text = typeof value === 'string' ? value : '';
    let units = 0;
    let encoding = 'GSM-7';
    const forcingUnicodeCharacters = [];
    for (const character of text) {
        if (GSM_BASIC.has(character)) units += 1;
        else if (GSM_EXTENSION.has(character)) units += 2;
        else {
            encoding = 'Unicode';
            units = [...text].length;
            if (!forcingUnicodeCharacters.includes(character)) forcingUnicodeCharacters.push(character);
        }
    }
    const perSegment = encoding === 'GSM-7' ? 160 : 70;
    const segments = text ? Math.ceil(units / perSegment) : 0;
    return { characters: [...text].length, units, encoding, perSegment, segments, forcingUnicodeCharacters };
}

function firstString(...values) {
    for (const value of values) {
        if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
}

export function getContactPhoneNumber(value) {
    return typeof value === 'string'
        ? value.trim()
        : firstString(value?.number, value?.phone, value?.value, value?.phone_number);
}

function normalizePhone(value) {
    return normalizeOutboundPhoneAddress(value).replace(/\D/g, '');
}

function normalizeEmail(value) {
    return firstString(value).toLowerCase();
}

export function normalizeOutboundPhoneAddress(value = '') {
    const clean = getContactPhoneNumber(value);
    if (!clean) return '';
    if (clean.startsWith('+')) return clean.replace(/[^\d+]/g, '');
    const digits = clean.replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    return clean;
}

function phoneSearchTokens(value) {
    const digits = normalizePhone(value);
    if (!digits) return [];
    const tokens = [digits];
    if (digits.length === 11 && digits.startsWith('1')) tokens.push(digits.slice(1));
    return tokens;
}

export function contactSearchText(contact) {
    const phones = Array.isArray(contact?.phones) ? contact.phones : [];
    return [
        contact?.name,
        ...(Array.isArray(contact?.emails) ? contact.emails : []),
        ...phones.map(getContactPhoneNumber),
        ...phones.flatMap(phoneSearchTokens),
    ].filter(Boolean).join(' ').toLowerCase();
}

export function contactMatchesSearch(contact, search = '') {
    const needles = firstString(search).toLowerCase().split(/\s+/).filter(Boolean);
    if (needles.length === 0) return true;
    const haystack = contactSearchText(contact);
    return needles.every((needle) => haystack.includes(needle));
}

export function contactHasWhatsApp({ contact, sessions = [] } = {}) {
    const phones = (Array.isArray(contact?.phones) ? contact.phones : [])
        .filter((phone) => phone && typeof phone === 'object');
    if (phones.some((phone) => phone.whatsapp === true)) return true;
    const phoneSet = new Set((Array.isArray(contact?.phones) ? contact.phones : []).map(normalizePhone).filter(Boolean));
    return (Array.isArray(sessions) ? sessions : []).some((session) => {
        if (!phoneSet.has(normalizePhone(session?.contact_phone))) return false;
        return [
            ...(Array.isArray(session?.raw_message_log) ? session.raw_message_log : []),
            ...(Array.isArray(session?.conversation_log) ? session.conversation_log : []),
        ].some((entry) => entry?.channel === 'whatsapp');
    });
}

function isMobilePhone(phone) {
    const kind = String(phone?.type || phone?.label || '').toLowerCase();
    return phone?.is_mobile === true || !kind || ['mobile', 'cell', 'cellular', 'mobile phone'].includes(kind);
}

export function contactReachOptions(contact, { sessions = [] } = {}) {
    const options = [];
    const phoneEntries = Array.isArray(contact?.phones) ? contact.phones.filter(isMobilePhone) : [];
    const phones = phoneEntries
        .map(normalizeOutboundPhoneAddress).filter((phone) => phone.startsWith('+'));
    const emails = Array.isArray(contact?.emails) ? contact.emails.map(firstString).filter(Boolean) : [];
    if (phones[0]) options.push({ key: `sms:${phones[0]}`, channel: 'sms', label: 'SMS', address: phones[0] });
    // BL-177 Step 2c (F6): WhatsApp is offered for every phone number. Without an open 24-hour window the composer forces an
    // approved template (isWhatsAppGeneralAllowed); it is never hidden just because there is no WhatsApp history yet.
    if (phones[0]) options.push({ key: `whatsapp:${phones[0]}`, channel: 'whatsapp', label: 'WhatsApp', address: phones[0] });
    if (emails[0]) options.push({ key: `email:${emails[0]}`, channel: 'email', label: 'Email', address: emails[0] });
    return options;
}

function contactMatchesSession(contact, session) {
    const phones = new Set((Array.isArray(contact?.phones) ? contact.phones : []).map(normalizePhone).filter(Boolean));
    const sessionPhone = normalizePhone(session?.contact_phone);
    const emails = new Set((Array.isArray(contact?.emails) ? contact.emails : []).map(normalizeEmail).filter(Boolean));
    const sessionEmail = normalizeEmail(session?.contact_email);
    if (sessionPhone && phones.has(sessionPhone)) return true;
    if (sessionEmail && emails.has(sessionEmail)) return true;
    const accountId = firstString(contact?.primary_account_id);
    return Boolean(!sessionPhone && accountId && session?.account_id === accountId);
}

const CHANNEL_NAMES = { sms: 'SMS', whatsapp: 'WhatsApp', email: 'Email' };

/**
 * Availability state for the New message composer. The UI hides channels with no recipient route or sender identity;
 * an applicable but temporarily blocked channel remains visible with its reason. `hint` is guidance on a usable channel.
 */
export function describeChannelAvailability({ channel, recipients = [], identities = [], sessions = [], now = Date.now() } = {}) {
    const name = CHANNEL_NAMES[channel] || channel;
    const forChannel = (Array.isArray(identities) ? identities : []).filter((identity) => identity.channel === channel);
    if (identities.length > 0 && forChannel.length === 0) {
        return { enabled: false, reason: channel === 'email' ? 'No email mailbox is connected yet.' : `${name}: no sending number is set up yet.` };
    }
    if (forChannel.length > 0 && forChannel.every((identity) => identity.blockedReason)) {
        return { enabled: false, reason: forChannel[0].blockedReason };
    }
    const list = Array.isArray(recipients) ? recipients : [];
    if (list.some(({ contact }) => !contactReachOptions(contact, { sessions }).some((option) => option.channel === channel))) {
        return { enabled: false, reason: channel === 'email' ? 'Email: this recipient has no email address.' : `${name}: this recipient has no mobile number.` };
    }
    if (channel === 'whatsapp' && list.length > 0
        && !list.every(({ contact }) => !contact?.isNewRecipient && isWhatsAppGeneralAllowed({ contact, sessions, now }))) {
        return { enabled: true, reason: '', hint: 'WhatsApp: first message must use an approved template.' };
    }
    return { enabled: true, reason: '' };
}

export function isWhatsAppGeneralAllowed({ contact, sessions = [], now = Date.now() } = {}) {
    const cutoff = Number(now) - WHATSAPP_FREEFORM_WINDOW_MS;
    return (Array.isArray(sessions) ? sessions : []).some((session) => {
        if (!contactMatchesSession(contact, session)) return false;
        const entries = Array.isArray(session?.raw_message_log) ? session.raw_message_log : [];
        return entries.some((entry) => {
            if (entry?.direction !== 'inbound' || entry?.channel !== 'whatsapp') return false;
            const receivedAt = new Date(entry.received_at || entry.at || entry.created_at).getTime();
            return Number.isFinite(receivedAt) && receivedAt >= cutoff;
        });
    });
}

// BL-177 Step 2m: this only READS the client's choices (texts opted out, email opted out, offers consent). Staff never write them:
// no builder in this file produces a preference or consent column, and the database refuses a staff write to those columns.
export function buildRecipientFanout({ contacts = [], channel = 'sms', messageType = 'general' } = {}) {
    return (Array.isArray(contacts) ? contacts : []).map((contact) => {
        const option = contactReachOptions(contact).find((candidate) => candidate.channel === channel);
        const optedOut = (channel === 'sms' || channel === 'mms') && Boolean(contact?.sms_opted_out_at);
        const emailOptedOut = channel === 'email' && Boolean(contact?.email_opted_out_at);
        const offerPreferences = contact?.communication_preferences?.offers_news;
        const missingMarketingConsent = messageType === 'campaign'
            && (channel === 'sms' || channel === 'mms' || channel === 'email')
            && !(channel === 'email'
                ? Boolean(offerPreferences?.enabled && offerPreferences?.by_email)
                : offerPreferences
                    ? Boolean(offerPreferences.enabled && offerPreferences.by_text)
                    : Boolean(contact?.sms_marketing_consent));
        return {
            contact,
            option: option || null,
            skipped: optedOut || emailOptedOut || missingMarketingConsent || !option,
            skippedReason: optedOut
                ? 'sms_opted_out'
                : (emailOptedOut ? 'email_opted_out' : (missingMarketingConsent ? 'missing_marketing_consent' : (!option ? 'missing_address' : null))),
        };
    });
}

// BL-177 PR C: an account is Business (named after the company) or Personal (named after the person's last name). `accountKind` wins; the
// older `isBusiness` flag still works for callers that have not moved over.
export function resolveAccountKind({ accountKind, isBusiness = true } = {}) {
    if (accountKind === 'personal' || accountKind === 'business') return accountKind;
    return isBusiness === false ? 'personal' : 'business';
}

export function buildNewAccountInsert({ accountName, entityId = null, isBusiness = true, accountKind } = {}) {
    const cleanAccountName = firstString(accountName);
    if (!cleanAccountName) throw new Error('Company / Account is required.');
    if (!entityId) throw new Error('A tenant entity is required before creating a contact.');
    return {
        entity_id: entityId,
        account_name: cleanAccountName,
        account_kind: resolveAccountKind({ accountKind, isBusiness }),
        requires_human_review: true,
        status: 'draft',
    };
}

export function buildNewContactInsert({
    name,
    firstName, lastName,
    phone = '',
    email = '',
    entityId = null,
    primaryAccountId = null,
    requiresHumanReview = false,
} = {}) {
    const cleanName = firstString(name);
    const cleanPhone = normalizeOutboundPhoneAddress(phone);
    const cleanEmail = firstString(email);
    if (!cleanName) throw new Error('Contact name is required.');
    if (!cleanPhone && !cleanEmail) throw new Error('Add a phone or email before messaging.');
    if (!entityId) throw new Error('A tenant entity is required before creating a contact.');
    const payload = {
        entity_id: entityId,
        name: cleanName,
        emails: cleanEmail ? [cleanEmail] : [],
        phones: cleanPhone ? [cleanPhone] : [],
    };
    if (primaryAccountId) payload.primary_account_id = primaryAccountId;
    if (firstName !== undefined) { payload.first_name = firstName.trim(); payload.last_name = (lastName || '').trim(); }
    if (requiresHumanReview) payload.requires_human_review = true;
    return payload;
}

export function buildAccountUpdate({ accountName, isBusiness = true, accountKind } = {}) {
    const cleanAccountName = firstString(accountName);
    if (!cleanAccountName) throw new Error('Company / Account is required.');
    return {
        account_name: cleanAccountName,
        account_kind: resolveAccountKind({ accountKind, isBusiness }),
    };
}

export function buildContactUpdate({
    name,
    firstName, lastName,
    phone = '',
    email = '',
    primaryAccountId = null,
    includeChannels = true,
} = {}) {
    const cleanName = firstString(name);
    const cleanPhone = normalizeOutboundPhoneAddress(phone);
    const cleanEmail = firstString(email);
    if (!cleanName) throw new Error('Contact name is required.');
    if (!cleanPhone && !cleanEmail && includeChannels) throw new Error('Add a phone or email before saving.');
    // includeChannels=false: phones and emails are saved as labeled entries (contact_methods), never as one-value arrays that would
    // replace the person's other numbers.
    const payload = includeChannels
        ? { name: cleanName, emails: cleanEmail ? [cleanEmail] : [], phones: cleanPhone ? [cleanPhone] : [] }
        : { name: cleanName };
    if (primaryAccountId) payload.primary_account_id = primaryAccountId;
    if (firstName !== undefined) { payload.first_name = firstName.trim(); payload.last_name = (lastName || '').trim(); }
    return payload;
}

export function buildNewContactPlaceholderName(address = '') {
    const cleanAddress = firstString(address);
    if (!cleanAddress) return 'New contact';
    // BL-177 Step 2d: a number reads formatted ("+49 163 5959331"), never raw E.164; an email stays as typed.
    return `New contact (${cleanAddress.includes('@') ? cleanAddress : (formatPhone(cleanAddress) || cleanAddress)})`;
}

export function isActiveCommunicationsSession(session) {
    const status = firstString(session?.approval_status).toLowerCase();
    return !TERMINAL_APPROVAL_STATUSES.includes(status);
}

export function findActiveThreadForContact(contact, sessions = []) {
    const accountId = firstString(contact?.primary_account_id);
    const phones = new Set((Array.isArray(contact?.phones) ? contact.phones : []).map(normalizePhone).filter(Boolean));
    const emails = new Set((Array.isArray(contact?.emails) ? contact.emails : []).map(normalizeEmail).filter(Boolean));

    return (Array.isArray(sessions) ? sessions : []).find((session) => {
        if (!isActiveCommunicationsSession(session)) return false;
        if (accountId && session?.account_id === accountId) return true;
        const sessionPhone = normalizePhone(session?.contact_phone);
        if (sessionPhone && phones.has(sessionPhone)) return true;
        const sessionEmail = normalizeEmail(session?.contact_email);
        return Boolean(sessionEmail && emails.has(sessionEmail));
    }) || null;
}

export function buildOutboundSessionInsert({ contact, option, sendingLine = '' } = {}) {
    const channel = firstString(option?.channel);
    const address = channel === 'email' ? firstString(option?.address) : normalizeOutboundPhoneAddress(option?.address);
    const subject = firstString(option?.subject, 'New message');
    if (channel !== 'email' && !address.startsWith('+')) throw new Error('Enter a phone number with + country code, or a 10-digit US number.');
    const base = {
        channel,
        account_id: contact?.primary_account_id || null,
        approval_status: 'pending',
        outcome: 'in_progress',
        capture_checklist: {},
        conversation_log: [],
        raw_message_log: [],
    };
    if (channel === 'email') {
        return {
            ...base,
            contact_email: address,
            email_subject: subject,
        };
    }
    const line = normalizeOutboundPhoneAddress(sendingLine);
    if (['sms', 'mms'].includes(channel) && (!/^\+[1-9]\d{7,14}$/.test(line) || line === RETIRED_SMS_LINE)) {
        throw new Error('A verified sending number is required to save this conversation.');
    }
    return {
        ...base,
        contact_phone: address,
        ...(['sms', 'mms'].includes(channel) ? { external_thread_id: `${address}:${line}` } : {}),
    };
}

// Commas, semicolons and newlines are unambiguous separators. Inside each chunk,
// collect formatted phone fragments only until a complete number is reached.
function recipientTokens(value) {
    return String(value || '').split(/[,;\r\n]+/).flatMap((chunk) => {
        const words = chunk.trim().split(/\s+/).filter(Boolean);
        const tokens = [];
        for (let i = 0; i < words.length; i += 1) {
            let token = words[i];
            if (/^[+\d().-]+$/.test(token)) {
                const complete = (text) => {
                    const digits = text.replace(/\D/g, '');
                    if (text.startsWith('+1')) return digits.length === 11;
                    return text.startsWith('+') ? digits.length >= 11 : digits.length === 10 || (digits.length === 11 && digits.startsWith('1'));
                };
                while (!complete(token) && i + 1 < words.length && /^[\d().-]+$/.test(words[i + 1]) && words[i + 1].replace(/\D/g, '').length < 10) {
                    token += ` ${words[++i]}`;
                }
            }
            tokens.push(token);
        }
        return tokens;
    });
}

/** Parse a pasted address list, resolve exact CRM addresses, and deduplicate by normalized delivery address. */
export function parseRecipientList(value, { contacts = [], selected = [], entityId = null } = {}) {
    const invalid = [];
    const added = [];
    const key = (address) => address.includes('@') ? address.toLowerCase() : normalizeOutboundPhoneAddress(address);
    const seen = new Set(selected.flatMap(({ contact }) => [
        ...(contact.phones || []).map(getContactPhoneNumber), ...(contact.emails || []),
    ]).map(key));
    for (const raw of recipientTokens(value)) {
        const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw);
        const address = email ? raw.toLowerCase() : normalizeOutboundPhoneAddress(raw);
        if (!email && (!/^[+\d().\s-]+$/.test(raw) || !/^(?:\+[1-9]\d{7,14})$/.test(address))) { invalid.push(raw); continue; }
        if (seen.has(key(address))) continue;
        const contact = contacts.find((candidate) => email
            ? (candidate.emails || []).some((item) => key(item) === key(address))
            : (candidate.phones || []).some((item) => key(getContactPhoneNumber(item)) === key(address))) || {
            id: `new:${email ? 'email' : 'phone'}:${address}`, name: buildNewContactPlaceholderName(address),
            phones: email ? [] : [address], emails: email ? [address] : [], entity_id: entityId,
            primary_account_id: null, isNewRecipient: true, saveAsContact: Boolean(entityId),
        };
        if (selected.some((item) => item.contact.id === contact.id) || added.some((item) => item.contact.id === contact.id)) continue;
        seen.add(key(address));
        added.push({ contact, address });
    }
    return { added, invalid };
}
